import { LegendItemVariants, LegendsLayout } from '@modules/charts/types';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { GetChartLabelOptions } from '@modules/common/types';
import { CircleRounded } from '@mui/icons-material';
import {
  Box,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  TypographyTypeMap,
} from '@mui/material';
import { round } from 'lodash';

interface LegendItemProps {
  id: string;
  label: string;
  index?: number;
  totalRecords?: number;
  itemValue?: number;
  layout: LegendsLayout;
  variant?: LegendItemVariants;
  color: string;
  getLabelOptions?: GetChartLabelOptions;
  onMouseEnter?: (event: React.MouseEvent, index: number) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
}

type TypographyVariant = TypographyTypeMap['props']['variant'];

export const LegendItem = ({
  id,
  label,
  index,
  totalRecords,
  itemValue,
  layout,
  color,
  variant = 'large',
  getLabelOptions,
  onMouseEnter,
  onMouseLeave,
}: LegendItemProps) => {
  const isHorizontalLayout = layout === 'horizontal';

  const smallLegendItemContainerStyle: SxProps<Theme> = {
    alignItems: 'baseline',
  };

  const largeLegendItemCircleStyle: SxProps<Theme> = {
    width: isHorizontalLayout ? 8 : 16,
    height: isHorizontalLayout ? 8 : 16,
  };

  const smallLegendItemCircleStyle: SxProps<Theme> = {
    width: 8,
    height: 8,
  };
  const smallLegendLabelStyle: SxProps<Theme> = {
    overflow: 'hidden',
    whiteSpace: 'block',
    textOverflow: 'unset',
  };

  const ContainerStyle: SxProps<Theme> = {
    ...(variant === 'small'
      ? smallLegendItemContainerStyle
      : { justifyContent: isHorizontalLayout ? 'center' : 'inherit' }),
  };

  const CircleStyle = {
    ...(variant === 'small'
      ? smallLegendItemCircleStyle
      : largeLegendItemCircleStyle),
  };

  const LabelStyle = {
    ...(variant === 'small' ? smallLegendLabelStyle : {}),
  };

  const getLabelVariantOnTheBasisOfLayout = () => {
    if (isHorizontalLayout) {
      return 'subtitle2';
    }
    return 'subtitle1';
  };

  const labelTypographyVariant: TypographyVariant =
    variant === 'small' ? 'body2' : getLabelVariantOnTheBasisOfLayout();

  const getLabelIconFontSize = (variant: TypographyVariant) => {
    switch (variant) {
      case 'subtitle1':
        return 14;
      case 'subtitle2':
        return 16;
      default:
        return 14;
    }
  };

  const legendValueTypographyVariant: TypographyVariant = isHorizontalLayout
    ? 'subtitle2'
    : 'subtitle1';

  const labelName = getChartLabelIdOrName(label);
  const labelOptions = getLabelOptions?.({
    id,
    size: getLabelIconFontSize(labelTypographyVariant),
    showIndentForEmptySecondaryIcon: !isHorizontalLayout,
  });

  const shouldShowTooltip =
    !onMouseEnter || !onMouseLeave || index === undefined;

  const tooltipTitle = shouldShowTooltip ? (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <Box sx={{ mt: -0.3 }}>{labelOptions?.icon}</Box>
      <Typography variant={labelTypographyVariant} color="inherit">
        {labelOptions?.label &&
          !isHorizontalLayout &&
          `${labelOptions?.label}: `}
        {labelName}
      </Typography>
    </Stack>
  ) : (
    ''
  );

  return (
    <Box
      width="100%"
      onMouseEnter={(event) => onMouseEnter?.(event, index ?? 0)}
      onMouseLeave={onMouseLeave}
    >
      <Stack
        direction="row"
        spacing={0.5}
        pr={1}
        sx={{
          ...ContainerStyle,
        }}
        alignItems="center"
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CircleRounded
            sx={{
              color,
              ...CircleStyle,
            }}
          />
          {itemValue && totalRecords ? (
            <Typography variant={legendValueTypographyVariant}>
              {round((itemValue / totalRecords) * 100, 2)}%
            </Typography>
          ) : null}
          <Stack>{labelOptions?.icon}</Stack>
        </Stack>
        <Tooltip title={tooltipTitle} disableInteractive placement="top">
          <Typography
            variant={labelTypographyVariant}
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              ...LabelStyle,
            }}
          >
            {labelName}
          </Typography>
        </Tooltip>
      </Stack>
    </Box>
  );
};
